import classNames from 'classnames'
import _ from 'lodash'
import React from 'react'
import { Classes } from '@blueprintjs/core'

import { UI2EntityPreviewCard } from 'browser/components/ui2/display/ui2-entity-preview-card/ui2-entity-preview-card'
import { useUI2Platform } from 'browser/components/ui2/forms/ui2-form/ui2-platform-context'
import {
  UI2PulseHighlight,
  IUI2PulseHighlightRef,
} from 'browser/components/ui2/behavior/pulse-highlight/pulse-highlight'
import { isEntityPreviewCardSchema } from 'shared-libs/components/ui2/fields/ui2-entity-search-field/entity-search-utils'

import { UI2Icon } from 'browser/components/ui2/display/ui2-icon/ui2-icon'
import { UI2IconName, UI2IconNames } from 'shared-libs/components/ui2/icons/ui2-icon-names'
import './_entity-search-selected-card.scss'

interface IEntitySearchSelectedCardProps {
  entity: any
  entitySchema: any
  entityPreviewCardSchemaPath?: string
  entityEditSchemaPath?: string
  entityReadOnlySchemaPath?: string
  /** Settings forwarded to the sheet the card opens. See `extraState` on the field. */
  extraState?: { [key: string]: any }
  /** Dot path on entity used as fallback display name. Defaults to 'displayName'. */
  optionValuePath?: string
  /** Icon name for the type stripe, sourced from the field's own iconName prop. */
  typeStripeIconName?: UI2IconName
  isDisabled?: boolean
  isReadOnly?: boolean
  isLoading?: boolean
  /** When true (set once at mount for isClearable:false + non-empty initial value), hides the clear / find-different action. */
  isValueLocked: boolean
  /** Pre-translated, built via `getFindDifferentLabel`. MW only. */
  findDifferentLabel: string
  /** Increments each time selection changes — key used to restart the card pulse animation. */
  pulseTick: number
  isVertical: boolean
  onClear: () => void
  /** Clears the selection and reopens the search. MW's replacement for `onClear`. */
  onFindDifferent: () => void
  /** Called when any entity form modal opened by the card is closed. */
  onModalClose?: () => void
}

export interface IEntitySearchSelectedCardRef {
  pulse: () => void
}

export const EntitySearchSelectedCard = React.forwardRef<IEntitySearchSelectedCardRef, IEntitySearchSelectedCardProps>(
function EntitySearchSelectedCard({
  entity,
  entitySchema,
  entityPreviewCardSchemaPath,
  entityEditSchemaPath,
  entityReadOnlySchemaPath,
  extraState,
  optionValuePath = 'displayName',
  typeStripeIconName,
  isDisabled,
  isReadOnly,
  isLoading,
  isValueLocked,
  findDifferentLabel,
  pulseTick,
  isVertical,
  onClear,
  onFindDifferent,
  onModalClose,
}: IEntitySearchSelectedCardProps, ref) {
  const isMobileWeb = useUI2Platform() === 'mobileWeb'
  const canChangeSelection = !isDisabled && !isLoading && !isReadOnly && !isValueLocked
  // MW has no room for a clear button beside the card, and clearing was never
  // the point — the user wants a different entity, which is one action rather
  // than a clear followed by a search. DW keeps the inline clear.
  const showClear = canChangeSelection && !isMobileWeb
  const showFindDifferent = canChangeSelection && isMobileWeb

  const pulseRef = React.useRef<IUI2PulseHighlightRef>(null)
  React.useImperativeHandle(ref, () => ({
    pulse: () => pulseRef.current?.pulse(),
  }))
  React.useEffect(() => {
    if (pulseTick > 0) pulseRef.current?.pulse()
  }, [pulseTick])

  const card = (
    <UI2PulseHighlight ref={pulseRef}>
    <div
      className={classNames('ui2-entitySearchField-selectedCard', {
        'ui2-entitySearchField-selectedCard--vertical': isVertical,
        'ui2-entitySearchField-selectedCard--disabled': isDisabled,
      })}
    >
      <div className='ui2-entitySearchField-selectedCard-body'>
        {renderEntityContent({
          entity,
          entitySchema,
          entityPreviewCardSchemaPath,
          entityEditSchemaPath,
          entityReadOnlySchemaPath,
          extraState,
          optionValuePath,
          typeStripeIconName,
          isDisabled,
          isReadOnly,
          onModalClose,
        })}
      </div>
      {showClear && (
        <div className='ui2-entitySearchField-selectedCard-actions'>
          <button
            type='button'
            className={classNames(Classes.MINIMAL, 'ui2-entitySearchField-clearIcon')}
            onClick={onClear}
            aria-label='Clear selection'
          >
            <UI2Icon name={UI2IconNames.X} />
          </button>
        </div>
      )}
    </div>
    </UI2PulseHighlight>
  )

  // The wrapper only appears when the find-different action does, so DW's DOM
  // is unchanged — in horizontal layout the shell's control cell styles its
  // direct child, and an unconditional wrapper would absorb those rules.
  if (!showFindDifferent) return card

  return (
    <div className='ui2-entitySearchField-selectedCardWrap'>
      {card}
      <button
        type='button'
        className='ui2-entitySearchField-findDifferent'
        onClick={onFindDifferent}
      >
        <UI2Icon name={UI2IconNames.SEARCH} size={14} />
        <span>{findDifferentLabel}</span>
      </button>
    </div>
  )
})

interface IRenderEntityContentArgs {
  entity: any
  entitySchema: any
  entityPreviewCardSchemaPath?: string
  entityEditSchemaPath?: string
  entityReadOnlySchemaPath?: string
  extraState?: { [key: string]: any }
  optionValuePath: string
  typeStripeIconName?: UI2IconName
  isDisabled?: boolean
  isReadOnly?: boolean
  onModalClose?: () => void
}

function renderEntityContent({
  entity,
  entitySchema,
  entityPreviewCardSchemaPath,
  entityEditSchemaPath,
  entityReadOnlySchemaPath,
  extraState,
  optionValuePath,
  typeStripeIconName,
  isDisabled,
  isReadOnly,
  onModalClose,
}: IRenderEntityContentArgs): React.ReactNode {
  const inlinePreview =
    entityPreviewCardSchemaPath && _.get(entitySchema, entityPreviewCardSchemaPath)

  if (inlinePreview && isEntityPreviewCardSchema(inlinePreview)) {
    const { type: _type, ...cardPropsFromSchema } = inlinePreview
    return (
      <UI2EntityPreviewCard
        entity={entity}
        entityEditSchemaPath={entityEditSchemaPath}
        entityReadOnlySchemaPath={entityReadOnlySchemaPath}
        extraState={extraState}
        typeStripeIconName={typeStripeIconName}
        isDisabled={isDisabled}
        isReadOnly={isReadOnly}
        onModalClose={onModalClose}
        {...cardPropsFromSchema}
      />
    )
  }

  if (inlinePreview) {
    // eslint-disable-next-line no-console
    console.warn(
      `[UI2EntitySearchField] entityPreviewCardSchemaPath "${entityPreviewCardSchemaPath}" ` +
        `is not a top-level ui:form:entityPreviewCard. Falling back to the default card.`
    )
  }

  return (
    <UI2EntityPreviewCard
      entity={entity}
      title={_.get(entity, optionValuePath) ?? entity?.displayName}
      entityEditSchemaPath={entityEditSchemaPath}
      entityReadOnlySchemaPath={entityReadOnlySchemaPath}
      typeStripeIconName={typeStripeIconName}
      isDisabled={isDisabled}
      isReadOnly={isReadOnly}
      onModalClose={onModalClose}
    />
  )
}
